import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listingsApi, bookingsApi, messagesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  MapPin, Star, Users, CheckCircle, Calendar as CalendarIcon, 
  ChevronLeft, ChevronRight, Loader2, Shield, Clock, MessageSquare
} from 'lucide-react';
import { formatCurrency, formatDate, getListingTypeLabel } from '../lib/utils';
import { toast } from 'sonner';

const defaultImage = 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=800';

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [bookingForm, setBookingForm] = useState({
    checkIn: null,
    checkOut: null,
    numAnimals: 1,
    message: '',
  });
  
  const [contactOpen, setContactOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await listingsApi.getById(id);
      setListing(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Listing not found');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async () => {
    if (!bookingForm.checkIn || !bookingForm.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setSubmitting(true);
    try {
      await bookingsApi.create({
        listing_id: id,
        check_in: bookingForm.checkIn.toISOString().split('T')[0],
        check_out: bookingForm.checkOut.toISOString().split('T')[0],
        num_animals: bookingForm.numAnimals,
        message: bookingForm.message,
      });
      toast.success('Booking request sent!');
      setBookingOpen(false);
      navigate('/trips');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotal = () => {
    if (!bookingForm.checkIn || !bookingForm.checkOut || !listing) return 0;
    const nights = Math.ceil((bookingForm.checkOut - bookingForm.checkIn) / (1000 * 60 * 60 * 24));
    return nights * listing.pricing.nightly * bookingForm.numAnimals;
  };

  const handleContactHost = async () => {
    if (!contactMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingMessage(true);
    try {
      await messagesApi.send({
        recipient_id: listing.host_id,
        content: contactMessage,
      });
      toast.success('Message sent to host!');
      setContactOpen(false);
      setContactMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  if (!listing) return null;

  const images = listing.photos?.length > 0 ? listing.photos : [defaultImage];
  const location = listing.location || {};

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pb-16" data-testid="listing-detail">
      {/* Image Gallery */}
      <div className="relative h-[50vh] md:h-[60vh] bg-farmhouse-paper">
        <img
          src={images[activeImageIndex]}
          alt={listing.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="absolute inset-0 gradient-overlay opacity-40" />
        
        {images.length > 1 && (
          <>
            <button
              onClick={() => setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-farmhouse-charcoal/80 rounded-full text-white hover:bg-farmhouse-charcoal transition-colors"
              data-testid="prev-image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-farmhouse-charcoal/80 rounded-full text-white hover:bg-farmhouse-charcoal transition-colors"
              data-testid="next-image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === activeImageIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 bg-farmhouse-charcoal/80 rounded-full text-white hover:bg-farmhouse-charcoal transition-colors"
          data-testid="back-button"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header Card */}
            <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
              <div className="flex flex-wrap gap-3 mb-4">
                <Badge className="bg-farmhouse-saddle/20 text-farmhouse-saddle border-0">
                  {getListingTypeLabel(listing.listing_type)}
                </Badge>
                {listing.is_verified && (
                  <Badge className="bg-farmhouse-moss/20 text-green-400 border-0">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>

              <h1 className="font-serif text-3xl md:text-4xl font-semibold text-farmhouse-cream mb-3">
                {listing.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-farmhouse-muted mb-4">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{location.city}, {location.state}</span>
                </div>
                {listing.average_rating > 0 && (
                  <div className="flex items-center gap-1 text-farmhouse-gold">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-medium">{listing.average_rating.toFixed(1)}</span>
                    <span className="text-farmhouse-muted">({listing.review_count} reviews)</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>Capacity: {listing.capacity}</span>
                </div>
              </div>

              <p className="text-farmhouse-cream leading-relaxed">
                {listing.description}
              </p>
            </div>

            {/* Details */}
            <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
              <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">Details</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-farmhouse-muted text-sm mb-1">Species Allowed</p>
                  <div className="flex flex-wrap gap-2">
                    {listing.species_allowed?.map((species) => (
                      <Badge key={species} variant="outline" className="capitalize border-farmhouse-subtle text-farmhouse-cream">
                        {species}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-farmhouse-muted text-sm mb-1">Address</p>
                  <p className="text-farmhouse-cream">
                    {location.city}, {location.state} {location.zip_code}
                  </p>
                </div>
              </div>

              {listing.amenities?.length > 0 && (
                <div className="mt-6">
                  <p className="text-farmhouse-muted text-sm mb-2">Amenities</p>
                  <div className="flex flex-wrap gap-2">
                    {listing.amenities.map((amenity) => (
                      <Badge key={amenity} variant="outline" className="border-farmhouse-subtle text-farmhouse-cream">
                        {amenity}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {listing.rules && (
                <div className="mt-6">
                  <p className="text-farmhouse-muted text-sm mb-2">Rules & Requirements</p>
                  <ul className="text-farmhouse-cream text-sm space-y-1">
                    {listing.rules.vaccination && <li>• Vaccination required</li>}
                    {listing.rules.quarantine && <li>• Quarantine period required</li>}
                    {listing.rules.owner_access && <li>• Owner access allowed</li>}
                  </ul>
                </div>
              )}
            </div>

            {/* Host Info */}
            {listing.host && (
              <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
                <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">About the Host</h2>
                <div className="flex items-center gap-4">
                  <div className="avatar w-16 h-16 text-xl bg-farmhouse-saddle">
                    {listing.host.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-farmhouse-cream font-medium text-lg">{listing.host.name}</p>
                    <p className="text-farmhouse-muted text-sm">
                      Member since {formatDate(listing.host.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews */}
            {listing.reviews?.length > 0 && (
              <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
                <h2 className="font-serif text-xl font-semibold text-farmhouse-cream mb-4">
                  Reviews ({listing.reviews.length})
                </h2>
                <div className="space-y-4">
                  {listing.reviews.map((review) => (
                    <div key={review.id} className="border-b border-farmhouse-subtle pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex text-farmhouse-gold">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'fill-current' : ''}`}
                            />
                          ))}
                        </div>
                        <span className="text-farmhouse-muted text-sm">
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                      <p className="text-farmhouse-cream">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6">
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-3xl font-serif font-semibold text-farmhouse-cream">
                  {formatCurrency(listing.pricing?.nightly || 0)}
                </span>
                <span className="text-farmhouse-muted">/ night</span>
              </div>

              {listing.pricing?.weekly && (
                <p className="text-farmhouse-muted text-sm mb-1">
                  Weekly: {formatCurrency(listing.pricing.weekly)}
                </p>
              )}
              {listing.pricing?.monthly && (
                <p className="text-farmhouse-muted text-sm mb-4">
                  Monthly: {formatCurrency(listing.pricing.monthly)}
                </p>
              )}

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-farmhouse-muted text-sm">
                  <Shield className="w-4 h-4 text-farmhouse-moss" />
                  <span>Verified host</span>
                </div>
                <div className="flex items-center gap-2 text-farmhouse-muted text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Usually responds within 24 hours</span>
                </div>
              </div>

              <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="btn-primary w-full mb-3" 
                    disabled={!isAuthenticated || user?.id === listing.host_id}
                    data-testid="request-booking-btn"
                  >
                    {user?.id === listing.host_id ? 'This is your listing' : 'Request to Book'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-farmhouse-paper border-farmhouse-subtle max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-farmhouse-cream">
                      Request to Book
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-farmhouse-cream mb-2 block">Check-in</Label>
                        <Calendar
                          mode="single"
                          selected={bookingForm.checkIn}
                          onSelect={(date) => setBookingForm({ ...bookingForm, checkIn: date })}
                          disabled={(date) => date < new Date()}
                          className="rounded-md border border-farmhouse-subtle"
                        />
                      </div>
                      <div>
                        <Label className="text-farmhouse-cream mb-2 block">Check-out</Label>
                        <Calendar
                          mode="single"
                          selected={bookingForm.checkOut}
                          onSelect={(date) => setBookingForm({ ...bookingForm, checkOut: date })}
                          disabled={(date) => date < (bookingForm.checkIn || new Date())}
                          className="rounded-md border border-farmhouse-subtle"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-farmhouse-cream">Number of Animals</Label>
                      <Input
                        type="number"
                        min={1}
                        max={listing.capacity}
                        value={bookingForm.numAnimals}
                        onChange={(e) => setBookingForm({ ...bookingForm, numAnimals: parseInt(e.target.value) })}
                        className="form-input mt-1"
                        data-testid="num-animals-input"
                      />
                    </div>

                    <div>
                      <Label className="text-farmhouse-cream">Message to Host</Label>
                      <Textarea
                        placeholder="Introduce yourself and your animals..."
                        value={bookingForm.message}
                        onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                        className="form-input mt-1 min-h-[100px]"
                        data-testid="booking-message"
                      />
                    </div>

                    {bookingForm.checkIn && bookingForm.checkOut && (
                      <div className="bg-farmhouse-charcoal p-4 rounded-md">
                        <div className="flex justify-between text-farmhouse-muted mb-2">
                          <span>
                            {formatCurrency(listing.pricing.nightly)} x {Math.ceil((bookingForm.checkOut - bookingForm.checkIn) / (1000 * 60 * 60 * 24))} nights x {bookingForm.numAnimals} animals
                          </span>
                        </div>
                        <div className="flex justify-between text-farmhouse-cream font-semibold text-lg">
                          <span>Total</span>
                          <span>{formatCurrency(calculateTotal())}</span>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleBookingSubmit}
                      className="btn-primary w-full"
                      disabled={submitting}
                      data-testid="submit-booking"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Request'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {!isAuthenticated && (
                <p className="text-center text-farmhouse-muted text-sm">
                  <a href="/auth" className="text-farmhouse-saddle hover:underline">Sign in</a> to request a booking
                </p>
              )}

              {/* Contact Host Button */}
              {isAuthenticated && user?.id !== listing.host_id && (
                <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full btn-secondary mt-3"
                      data-testid="contact-host-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Contact Host
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-farmhouse-paper border-farmhouse-subtle">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-2xl text-farmhouse-cream">
                        Message {listing.host?.name || 'Host'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-farmhouse-cream">Your Message</Label>
                        <Textarea
                          placeholder="Ask about the listing, availability, or introduce yourself..."
                          value={contactMessage}
                          onChange={(e) => setContactMessage(e.target.value)}
                          className="form-input mt-2 min-h-[120px]"
                          data-testid="contact-message-input"
                        />
                      </div>
                      <Button
                        onClick={handleContactHost}
                        className="btn-primary w-full"
                        disabled={sendingMessage || !contactMessage.trim()}
                        data-testid="send-contact-message"
                      >
                        {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Message'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
